import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";

import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";

import { createMemoryHistory } from "history";
import * as serviceWorker from "./serviceWorker";
import { ThemeProvider } from "@material-ui/core/styles";

import configureStore from "./store/configureState";
import { ConnectedRouter } from "connected-react-router";

import { getBrandedConfiguration } from "./brandConfigurator";

import { ApplicationState } from "./store";

import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import svLocale from "date-fns/locale/sv";
import { BrandsEnum } from "./enums";
import PageEventsProvider from "./pages/PageEventsProvider";
import FontLoader from "./fontLoader";

type InitProps = {
  apiHost?: string;
  cmsHost?: string;
  elementId: string;
};

export type Language = "SV" | "NO" | "FI" | "DA" | "EN" | "ET" | "LT" | "LV" | "RU";
export type Country = "Sweden" | "Norway" | "Denmark" | "Finland" | "Latvia" | "Lithuania" | "Estonia";

const defaults: InitProps = {
  apiHost: "",
  cmsHost: "",
  elementId: "root",
};

const init = (params?: InitProps) => {
  params = params ? { ...defaults, ...params } : defaults;

  const element: HTMLElement | null = document.getElementById(params.elementId);

  const history = createMemoryHistory({});
  history.listen(() => {
    window?.scrollTo(0, 0);
  });

  const brand = element?.getAttribute("partner") ? (element?.getAttribute("partner") as BrandsEnum) : BrandsEnum.Polestar;
  const isEOffer = element?.getAttribute("webshopMode") ?? "1";
  const calculationMethod = element?.getAttribute("calculationMethod") ?? "0";
  const disableBuying = element?.getAttribute("disableBuying") ?? "no";

  const checkIfCommercial = () => {
    const ssn = element?.getAttribute("ssn") ?? "";
    const orn = element?.getAttribute("orn");
    const orgNoRegEx = /^([5]\d{5}[-]\d{4})|([5]\d{9})$/;

    if (orn || orgNoRegEx.test(ssn)) {
      const monthCharacters = ssn.substring(2, 4);
      if (Number(monthCharacters) >= 20) {
        return true;
      }
      return false;
    }

    return false;
  };

  const toBoolean = (value: string | number | boolean): boolean => [true, "true", "True", "TRUE", "1", 1].includes(value);

  const language: Language = (element?.getAttribute("language")?.toUpperCase() as Language) ?? "SV";

  const country: Country = (element?.getAttribute("country") as Country) ?? "Sweden";

  let store = configureStore(history, {
    ui: { initialized: false, completed: false },
    // take care about inputs injection
    inputs: {
      crn: element?.getAttribute("crn") ?? "",
      orn: element?.getAttribute("orn"),
      ssn: element?.getAttribute("ssn") ?? "",
      firstName: element?.getAttribute("firstName") ?? "",
      lastName: element?.getAttribute("lastName") ?? "",
      redirectLink: element?.getAttribute("callbackUrl"),
      errorUrl: element?.getAttribute("errorUrl"),
      apiHost: params.apiHost?.replace(/\/$/, ""),
      cmsHost: params.cmsHost?.replace(/\/$/, ""),
      webshopPageGuid: element?.getAttribute("webshopPageGuid") ?? "",
      orderId: element?.getAttribute("orderId"),
      quoteId: element?.getAttribute("quoteId") ?? undefined,
      isRemote: element?.getAttribute("isRemote") === "remote" ? true : false,
      isKamux: toBoolean(element?.getAttribute("isKamux") ?? false),
      isAfpris: toBoolean(element?.getAttribute("isAfpris") ?? false),
      partner: brand,
      brand: element?.getAttribute("brand") ?? "",
      webshopMode: parseInt(isEOffer),
      campaignId: element?.getAttribute("campaignId") ?? "",
      disableBuying: toBoolean(disableBuying),
      calculationMethod: parseInt(calculationMethod),
      confirmationPageUrl: element?.getAttribute("webshopConfirmationPageUrl") ?? "",
      car: {
        code: element?.getAttribute("carCode") ?? "",
        model: element?.getAttribute("carModel") ?? "",
        variant: element?.getAttribute("carVariant") ?? "",
        yearFrom: element?.getAttribute("carYearFrom") ?? "",
        yearTo: element?.getAttribute("carYearTo") ?? "",
      },
      quoteNumber: element?.getAttribute("quoteNumber"),
      useCarApiV2: toBoolean(element?.getAttribute("UseCarApiV2") ?? "false"),
      isCommercial: checkIfCommercial(),
      language,
      country,
      decoupledRendering: {
        mainLogo: element?.getAttribute("mainLogo") ?? "",
        mainLogoAlt: element?.getAttribute("mainLogoAlt") ?? "",
        secondaryLogo: element?.getAttribute("secondaryLogo") ?? "",
        secondaryLogoAlt: element?.getAttribute("secondaryLogoAlt") ?? "",
        isDecoupledRendering: toBoolean(element?.getAttribute("decoupledRendering") ?? false),
      },
    },
  } as ApplicationState);
  // change 'partner' attribute in index.html to see the difference in routes and theme!
  const brandedConfig = getBrandedConfiguration(brand);
  const locale: Locale = svLocale;

  if ((!store.getState().inputs.ssn || !store.getState().inputs.orn) && !store.getState().inputs.crn) {
    const sessionData = JSON.parse(sessionStorage.getItem("store") ?? "{}");
    store = configureStore(history, {
      ...sessionData,
    } as ApplicationState);
  } else {
    sessionStorage.setItem("store", JSON.stringify(store.getState()));
  }

  FontLoader(brand);

  const routes = brandedConfig.Routes;

  ReactDOM.render(
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
      <Provider store={store}>
        <ThemeProvider theme={brandedConfig.Theme}>
          <PageEventsProvider>
            <ConnectedRouter history={history}>{routes}</ConnectedRouter>
          </PageEventsProvider>
        </ThemeProvider>
      </Provider>
    </MuiPickersUtilsProvider>,
    element,
  );

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA
  serviceWorker.unregister();
};

declare global {
  interface Window {
    op_webshop: Function;
    dataLayer?: any[];
  }
}

window.op_webshop = window.op_webshop ?? ((params?: InitProps) => init(params));
